<script setup>
import { computed, onMounted, onUnmounted, watch } from "vue";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    message: {
        type: String,
        default: "",
    },
    closeable: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["close"]);

watch(
    () => props.show,
    () => {
        if (props.show) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = null;
        }
    },
);

const close = () => {
    if (props.closeable) {
        emit("close");
    }
};

const closeOnEscape = (e) => {
    if (e.key === "Escape" && props.show) {
        close();
    }
};

onMounted(() => document.addEventListener("keydown", closeOnEscape));

onUnmounted(() => {
    document.removeEventListener("keydown", closeOnEscape);
    document.body.style.overflow = null;
});
</script>

<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div
                v-show="show"
                class="fixed inset-0 z-50 flex items-center justify-center bg-red-900/90 p-4"
            >
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                >
                    <div
                        v-show="show"
                        class="relative w-full max-w-md overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-red-500"
                    >
                        <!-- Header -->
                        <div class="bg-red-50 px-6 py-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <ExclamationTriangleIcon
                                        class="mr-3 size-8 text-red-600"
                                        aria-hidden="true"
                                    />
                                    <h3 class="text-lg font-semibold text-red-900">Error</h3>
                                </div>
                                <button
                                    v-if="closeable"
                                    type="button"
                                    class="rounded-md p-1 text-red-400 hover:bg-red-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    @click="close"
                                >
                                    <span class="sr-only">Close</span>
                                    <XMarkIcon
                                        class="size-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </div>

                        <!-- Content -->
                        <div class="px-6 py-4">
                            <p
                                class="text-sm text-gray-700"
                                v-html="message"
                            ></p>
                        </div>

                        <!-- Footer -->
                        <div class="bg-gray-50 px-6 py-3">
                            <div class="flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    @click="close"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>
